import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Package, MapPin, User, Phone, Clock, CheckCircle, Loader, AlertCircle, Image as ImageIcon, Key, Copy, Eye, EyeOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';
import api from '../utils/api';

const DeliveryHistory = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, completed
  const [showOTP, setShowOTP] = useState({}); // { deliveryId: true/false }

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      const response = await api.get('/deliveries/my-deliveries');
      setDeliveries(response.data.deliveries || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load delivery history',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const toggleOTP = (deliveryId) => {
    setShowOTP(prev => ({
      ...prev,
      [deliveryId]: !prev[deliveryId]
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: `${label} copied to clipboard` });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500', text: 'Pending Pickup' },
      accepted: { color: 'bg-blue-500', text: 'Driver Assigned' },
      in_progress: { color: 'bg-purple-500', text: 'In Transit' },
      completed: { color: 'bg-green-500', text: 'Delivered' },
      cancelled: { color: 'bg-red-500', text: 'Cancelled' },
    };

    const config = statusConfig[status] || { color: 'bg-gray-500', text: status };

    return (
      <Badge className={`${config.color} text-white`}>
        {config.text}
      </Badge>
    );
  };

  const filteredDeliveries = deliveries.filter((delivery) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['pending', 'accepted', 'in_progress'].includes(delivery.status);
    if (filter === 'completed') return delivery.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader className="h-12 w-12 animate-spin text-pink-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Deliveries</h1>
                <p className="text-gray-600 mt-1">Track your package deliveries</p>
              </div>
              <Button
                onClick={() => navigate('/')}
                className="bg-pink-600 hover:bg-pink-700"
              >
                <Package className="h-4 w-4 mr-2" />
                Send New Package
              </Button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
              {['all', 'pending', 'completed'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filter === f
                      ? 'bg-pink-600 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Deliveries List */}
          {filteredDeliveries.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Package className="h-8 w-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {filter === 'all' ? 'No Deliveries Yet' : `No ${filter} deliveries`}
              </h2>
              <p className="text-gray-600 mb-4">
                {filter === 'all' 
                  ? "You haven't sent any packages yet. Start by sending your first package!"
                  : `You don't have any ${filter} deliveries at the moment.`
                }
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-pink-600 hover:bg-pink-700"
              >
                Send a Package
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredDeliveries.map((delivery) => (
                <Card key={delivery.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white">
                          <Package className="h-6 w-6" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Delivery #{delivery.id.slice(0, 8)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(delivery.createdAt).toLocaleDateString()} at{' '}
                            {new Date(delivery.createdAt).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(delivery.status)}
                    </div>

                    {/* Package Details */}
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="text-sm text-gray-600">Package</p>
                          <p className="font-medium">
                            <span className="capitalize">{delivery.packageDetails?.size}</span>
                            {' - '}
                            {delivery.packageDetails?.description}
                          </p>
                          {delivery.packageDetails?.fragile && (
                            <div className="flex items-center gap-1 text-orange-600 text-sm mt-1">
                              <AlertCircle className="h-3 w-3" />
                              <span>Fragile</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Receiver</p>
                          <p className="font-medium">{delivery.receiverDetails?.name}</p>
                          <p className="text-sm text-gray-500">{delivery.receiverDetails?.phone}</p>
                        </div>
                      </div>
                    </div>

                    {/* Route */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5"></div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">Pickup</p>
                          <p className="font-medium text-gray-900">{delivery.pickupLocation}</p>
                        </div>
                      </div>
                      <div className="h-8 border-l-2 border-dashed border-gray-300 ml-1.5"></div>
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5"></div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">Delivery</p>
                          <p className="font-medium text-gray-900">{delivery.dropoffLocation}</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver Info (if assigned) */}
                    {delivery.driverName && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-sm text-gray-600 mb-1">Driver</p>
                        <p className="font-medium text-gray-900">{delivery.driverName}</p>
                      </div>
                    )}

                    {/* OTP Section - Show for non-completed deliveries */}
                    {delivery.status !== 'completed' && delivery.status !== 'cancelled' && (
                      <div className="border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleOTP(delivery.id)}
                          className="mb-3"
                          data-testid={`toggle-otp-${delivery.id}`}
                        >
                          {showOTP[delivery.id] ? (
                            <>
                              <EyeOff className="h-4 w-4 mr-2" />
                              Hide OTP Codes
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-2" />
                              Show OTP Codes
                            </>
                          )}
                        </Button>

                        {showOTP[delivery.id] && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Pickup OTP */}
                            <div className={`p-3 rounded-lg border ${delivery.pickupOTP?.verified ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Key className={`h-4 w-4 ${delivery.pickupOTP?.verified ? 'text-green-600' : 'text-blue-600'}`} />
                                <span className={`text-sm font-semibold ${delivery.pickupOTP?.verified ? 'text-green-800' : 'text-blue-800'}`}>
                                  Pickup OTP {delivery.pickupOTP?.verified && '✓'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`text-xl font-mono font-bold ${delivery.pickupOTP?.verified ? 'text-green-900' : 'text-blue-900'}`}>
                                  {delivery.pickupOTP?.code || 'N/A'}
                                </span>
                                {!delivery.pickupOTP?.verified && delivery.pickupOTP?.code && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(delivery.pickupOTP.code, 'Pickup OTP')}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <p className={`text-xs mt-1 ${delivery.pickupOTP?.verified ? 'text-green-700' : 'text-blue-700'}`}>
                                {delivery.pickupOTP?.verified ? 'Verified ✓' : 'Give to biker at pickup'}
                              </p>
                            </div>

                            {/* Delivery OTP */}
                            <div className={`p-3 rounded-lg border ${delivery.deliveryOTP?.verified ? 'bg-green-50 border-green-200' : 'bg-purple-50 border-purple-200'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Key className={`h-4 w-4 ${delivery.deliveryOTP?.verified ? 'text-green-600' : 'text-purple-600'}`} />
                                <span className={`text-sm font-semibold ${delivery.deliveryOTP?.verified ? 'text-green-800' : 'text-purple-800'}`}>
                                  Delivery OTP {delivery.deliveryOTP?.verified && '✓'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`text-xl font-mono font-bold ${delivery.deliveryOTP?.verified ? 'text-green-900' : 'text-purple-900'}`}>
                                  {delivery.deliveryOTP?.code || 'N/A'}
                                </span>
                                {!delivery.deliveryOTP?.verified && delivery.deliveryOTP?.code && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(delivery.deliveryOTP.code, 'Delivery OTP')}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <p className={`text-xs mt-1 ${delivery.deliveryOTP?.verified ? 'text-green-700' : 'text-purple-700'}`}>
                                {delivery.deliveryOTP?.verified ? 'Verified ✓' : `Share with ${delivery.receiverDetails?.name || 'receiver'}`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Proof of Delivery */}
                    {delivery.proofOfDelivery && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-semibold text-green-900">Proof of Delivery</span>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm">
                            <span className="text-gray-600">Received by:</span>{' '}
                            <span className="font-medium">{delivery.proofOfDelivery.receiverName}</span>
                          </p>
                          {delivery.proofOfDelivery.notes && (
                            <p className="text-sm">
                              <span className="text-gray-600">Notes:</span>{' '}
                              <span className="text-gray-900">{delivery.proofOfDelivery.notes}</span>
                            </p>
                          )}
                          {delivery.proofOfDelivery.photoUrl && (
                            <div>
                              <p className="text-sm text-gray-600 mb-2">Delivery Photo:</p>
                              <img
                                src={delivery.proofOfDelivery.photoUrl}
                                alt="Proof of delivery"
                                className="rounded-lg border border-gray-300 max-w-xs"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">{delivery.distance?.toFixed(1)} km</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-green-600">K{delivery.price}</span>
                        </div>
                      </div>
                      {delivery.status === 'in_progress' && (
                        <Button
                          onClick={() => navigate('/tracking', { state: { rideId: delivery.id } })}
                          variant="outline"
                          size="sm"
                        >
                          Track Delivery
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DeliveryHistory;
